import {
  Component, OnInit, ViewChild, OnDestroy,
  ElementRef, AfterViewInit, ChangeDetectorRef, HostListener
} from "@angular/core";
import { ROUTES } from './vertical-menu-routes.config';

import { Router } from "@angular/router";
import { TranslateService } from '@ngx-translate/core';
import { customAnimations } from "../animations/custom-animations";
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConfigService } from '../services/config.service';
import { Subscription } from 'rxjs';
import { LayoutService } from '../services/layout.service';
import { NavbarComponent } from "../navbar/navbar.component";
import { AuthService } from "app/service/auth/auth.service";
import { tblUserMaster } from "app/models/user/tblusermaster";
import { Role } from "app/service/enum/role";
import { EnhancedMenuService } from "app/service/enhanced-menu.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./vertical-menu.component.html",
  styleUrls: ["./vertical-menu.component.scss"],
  animations: customAnimations
})
export class VerticalMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('toggleIcon') toggleIcon!: ElementRef;
  public menuItems: any[] = [];
  level: number = 0;
  logoUrl = 'assets/img/logo.png';
  public config: any = {};
  protected innerWidth: any;
  layoutSub!: Subscription;
  configSub!: Subscription;
  menuRefreshSub!: Subscription;
  perfectScrollbarEnable = true;
  collapseSidebar = false;
  resizeTimeout: any;
  public menuName!: string;
  public loggedInUser: tblUserMaster = new tblUserMaster();
  public renderItems: any[] = [];
  public lucideLoaded = false;
  private lucideTimer: any;
  public isMenuLoading = true;

  constructor(
    private router: Router,
    public translate: TranslateService,
    private layoutService: LayoutService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
    private deviceService: DeviceDetectorService,
    private authService: AuthService,
    private enhancedMenuService: EnhancedMenuService,
  ) {
    this.config = this.configService.templateConf;
    this.innerWidth = window.innerWidth;
    this.isTouchDevice();
  }


  async ngOnInit() {
    this.loggedInUser = this.authService.getLoggedInUser();
    this.menuRefreshSub = this.enhancedMenuService.menuRefresh$.subscribe(() => {
      void this.loadMenuItems();
    });
    await this.loadMenuItems();
  }

  private async loadLucideScript(): Promise<void> {
    if (this.lucideLoaded) return;
    const w = window as any;
    if (w?.lucide?.createIcons) {
      this.lucideLoaded = true;
      return;
    }
    await new Promise<void>((resolve) => {
      const existing = document.querySelector('script[data-lucide-cdn="1"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => resolve());
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/lucide@latest/dist/umd/lucide.min.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-lucide-cdn', '1');
      s.onload = () => resolve();
      s.onerror = () => {
        const s2 = document.createElement('script');
        s2.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
        s2.async = true;
        s2.defer = true;
        s2.setAttribute('data-lucide-cdn', '1');
        s2.onload = () => resolve();
        s2.onerror = () => resolve();
        document.head.appendChild(s2);
      };
      document.head.appendChild(s);
    });
    this.lucideLoaded = !!((window as any)?.lucide?.createIcons);
  }

  public queueLucideCreateIcons(): void {
    return;
  }

  public getInitials(): string {
    const first = (this.loggedInUser?.firstName || '').trim();
    const last = (this.loggedInUser?.lastName || '').trim();
    const a = first ? first[0] : '';
    const b = last ? last[0] : '';
    const v = `${a}${b}`.trim();
    return v ? v.toUpperCase() : 'U';
  }

  public getDisplayName(): string {
    const name = `${this.loggedInUser?.firstName || ''} ${this.loggedInUser?.middleName || ''} ${this.loggedInUser?.lastName || ''}`.replace(/\s+/g, ' ').trim();
    return name || 'User';
  }

  public getRoleLabel(): string {
    const roleId = this.loggedInUser?.currentRoleId;
    const label = roleId !== undefined ? (Role as any)[roleId] : undefined;
    return (label || 'User').toString().replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
  }

  public menuLucideIcon(item: any): string {
    const title = (item?.title || item?.displayName || '').toString().toLowerCase();
    const path = (item?.path || '').toString().toLowerCase();
    if (title.includes('dashboard')) return 'layout-dashboard';
    if (title === 'user') return 'user';
    if (title.includes('user application')) return 'user-check';
    if (title === 'super admin') return 'shield';
    if (title === 'checker') return 'badge-check';
    if (title === 'maker') return 'pencil';
    if (title.includes('ingredients')) return 'sprout';
    if (title.includes('finish product')) return 'package';
    if (title === 'recipe' || title.includes('recipes')) return 'chef-hat';
    if (title === 'ingredient master' || title.includes('ingredient master')) return 'layers';
    if (title.includes('master data')) return 'database';
    if (title.includes('billing')) return 'receipt';
    if (title.includes('supplement')) return 'pill';
    if (title.includes('view recipe')) return 'book-open';
    if (title.includes('configuration')) return 'settings-2';
    if (title.includes('reports')) return 'bar-chart-3';
    if (title.includes('system settings')) return 'settings';
    return 'circle';
  }

  public onMenuClick(item: any): void {
    const path = (item?.path || '').toString().toLowerCase();
    if (path.includes('rawingredient')) {
      sessionStorage.removeItem('rdbms_raw_ingredients_list_state');
    }
    if (path.includes('ingredient-master')) {
      sessionStorage.removeItem('rdbms_ingredient_master_list_state');
    }
    if (path.includes('finishproduct')) {
      sessionStorage.removeItem('rdbms_finish_product_list_state');
    }
    if (path.includes('recipe')) {
      sessionStorage.removeItem('rdbms_recipe_list_state');
    }
    if (path.includes('supplement')) {
      sessionStorage.removeItem('rdbms_supplement_list_state');
    }
  }

  public menuFeatherIcon(item: any): string {
    const title = (item?.title || item?.displayName || '').toString().toLowerCase();
    const path = (item?.path || '').toString().toLowerCase();
    if (title.includes('dashboard')) return 'ft-grid';
    if (title === 'user') return 'ft-user';
    if (title.includes('user application')) return 'ft-user-check';
    if (title === 'super admin') return 'ft-shield';
    if (title === 'checker') return 'ft-check-circle';
    if (title === 'maker') return 'ft-edit-2';
    if (title.includes('ingredients')) return 'ft-layers';
    if (title.includes('finish product')) return 'ft-box';
    if (title === 'recipe' || title.includes('recipes')) return 'ft-archive';
    if (title === 'ingredient master' || title.includes('ingredient master')) return 'ft-server';
    if (title.includes('master data')) return 'ft-database';
    if (title.includes('billing')) return 'ft-credit-card';
    if (title.includes('supplement')) return 'ft-package';
    if (title.includes('view recipe')) return 'ft-book-open';
    if (title.includes('configuration')) return 'ft-sliders';
    if (title.includes('reports')) return 'ft-bar-chart-2';
    if (title.includes('system settings')) return 'ft-settings';
    return 'ft-circle';
  }

  /**
   * UX cleanup requested: move Finish Product -> (Super Admin / Checker / Maker) out of Finish Product submenu.
   * We keep their child routes intact, but render these 3 as separate top-level groups.
   */
  private getFinishProductLandingPath(): string {
    const currentRoleId = this.loggedInUser?.currentRoleId || this.loggedInUser?.roleIds?.[0];

    switch (currentRoleId) {
      case Role.Approver:
        return '/finishproduct/approver/finishproduct';
      case Role.Assigner:
        return '/finishproduct/assigner/finishproduct';
      case Role.Checker:
        return '/finishproduct/checker/finishproduct';
      case Role.Maker:
        return '/finishproduct/maker/finishproduct';
      default:
        return '/finishproduct/superadmin/all';
    }
  }

  private getSupplementLandingPath(): string {
    const currentRoleId = this.loggedInUser?.currentRoleId || this.loggedInUser?.roleIds?.[0];

    switch (currentRoleId) {
      case Role.Approver:
        return '/supplement/approver/supplements';
      case Role.Assigner:
        return '/supplement/assigner/supplements';
      case Role.Checker:
        return '/supplement/checker/supplements';
      case Role.Maker:
        return '/supplement/maker/recipelist';
      default:
        return '/supplement/superadmin/all';
    }
  }

  private getFlattenedRoleMenuConfig(roleId?: number): { groupTitle: string; billingPath: string } | null {
    switch (roleId) {
      case Role.Checker:
        return { groupTitle: 'checker', billingPath: '/finishproduct/checker/billing' };
      case Role.Maker:
        return { groupTitle: 'maker', billingPath: '/finishproduct/maker/billing' };
      default:
        return null;
    }
  }

  private promoteBillingAfterFinishProduct(
    items: any[],
    finishIdx: number,
    extractedRoleGroups: any[],
    config: { groupTitle: string; billingPath: string }
  ): void {
    const roleGroup = extractedRoleGroups.find((g: any) =>
      ((g?.title || '') as string).toLowerCase().trim() === config.groupTitle
    );
    const roleSubmenu = Array.isArray(roleGroup?.submenu) ? roleGroup.submenu : [];
    const billingItem = roleSubmenu.find((s: any) =>
      ((s?.path || '') as string).toLowerCase().includes('/billing')
    );

    if (billingItem) {
      items.splice(finishIdx + 1, 0, {
        ...billingItem,
        title: 'Billing',
        path: billingItem.path || config.billingPath,
        icon: 'assets/img/dashbord/icon/billing.png',
        class: (billingItem?.class || '').replace(/\bhas-sub\b/, '').trim(),
        submenu: [],
      });
    }
  }

  private normalizeMenu(items: any[]): any[] {
    const list = Array.isArray(items) ? items : [];
    const out: any[] = [...list];

    // Requirement: Show a single "Finish Product" tab that opens listing.
    // Super Admin keeps role-group dropdowns; Maker/Checker get Finish Product + Billing at top-level.
    const finishIdx = out.findIndex((it) =>
      ((it?.title || '') as string).toLowerCase().trim().includes('finish product')
    );
    if (finishIdx !== -1) {
      const originalFinish = out[finishIdx];
      const originalSubmenu = Array.isArray(originalFinish?.submenu) ? originalFinish.submenu : [];
      // Extract role groups before flattening Finish Product
      const roleTitles = new Set(['super admin', 'approver', 'assigner', 'checker', 'maker']);
      const currentRoleId = this.loggedInUser?.currentRoleId || this.loggedInUser?.roleIds?.[0];
      const extractedRoleGroups = originalSubmenu.filter((s: any) => {
        const title = ((s?.title || '') as string).toLowerCase().trim();
        if (!roleTitles.has(title)) {
          return false;
        }
        if (currentRoleId === Role.SuperAdmin) {
          return true;
        }
        if (currentRoleId === Role.Approver) {
          return title === 'approver';
        }
        if (currentRoleId === Role.Assigner) {
          return title === 'assigner';
        }
        if (currentRoleId === Role.Checker) {
          return title === 'checker';
        }
        if (currentRoleId === Role.Maker) {
          return title === 'maker';
        }
        return false;
      });

      const finish = { ...originalFinish };
      finish.path = this.getFinishProductLandingPath();
      finish.submenu = [];
      finish.class = (finish?.class || '').replace(/\bhas-sub\b/, '').trim();
      out[finishIdx] = finish;

      const flattenedRoleMenu = this.getFlattenedRoleMenuConfig(currentRoleId);
      if (flattenedRoleMenu) {
        this.promoteBillingAfterFinishProduct(out, finishIdx, extractedRoleGroups, flattenedRoleMenu);
      } else if (extractedRoleGroups.length) {
        const insertAt = finishIdx + 1;
        out.splice(
          insertAt,
          0,
          ...extractedRoleGroups.map((g: any) => {
            const title = (g?.title || '').toString().toLowerCase().trim();
            let submenuItems = Array.isArray(g?.submenu) ? g.submenu : [];
            if (title === 'super admin') {
              submenuItems = submenuItems.filter((s: any) => {
                const st = (s?.title || '').toString().toLowerCase().trim();
                return st !== 'super admin master';
              });
            }
            return {
              ...g,
              class: (g?.class || '').includes('has-sub') ? g.class : `${g?.class || ''} has-sub`.trim(),
              submenu: submenuItems,
            };
          })
        );
      }
    }

    const currentRoleId = this.loggedInUser?.currentRoleId || this.loggedInUser?.roleIds?.[0];
    const hiddenRoleTab = this.getFlattenedRoleMenuConfig(currentRoleId)?.groupTitle;
    if (hiddenRoleTab) {
      return out.filter((it) => ((it?.title || '') as string).toLowerCase().trim() !== hiddenRoleTab);
    }

    // Normalize Supplements: single link, role-aware path
    const supplementIdx = out.findIndex((it) =>
      ((it?.title || '') as string).toLowerCase().trim() === 'supplements'
    );
    if (supplementIdx !== -1) {
      const supplementItem = { ...out[supplementIdx] };
      supplementItem.path = this.getSupplementLandingPath();
      supplementItem.submenu = [];
      supplementItem.class = (supplementItem?.class || '').replace(/\bhas-sub\b/, '').trim();
      out[supplementIdx] = supplementItem;
    }

    // Do not change anything else
    return out;
  }

  private cloneMenuItem(item: any): any {
    return {
      ...item,
      submenu: Array.isArray(item?.submenu) ? item.submenu.map((child: any) => this.cloneMenuItem(child)) : []
    };
  }

  private buildRenderItems(items: any[]): any[] {
    const out: any[] = [];
    const pushHeader = (label: string) => out.push({ __type: 'header', label });
    const pushItem = (it: any) => out.push({ __type: 'item', ...it });

    const list = this.normalizeMenu(items);
    const userTitles = new Set(['user', 'user application', 'users', 'userapplications']);

    // Main
    pushHeader('MAIN');
    list.forEach((it) => {
      const t = (it?.title || '').toString().toLowerCase();
      if (t.includes('dashboard')) pushItem(it);
    });

    // User management
    const users = list.filter((it) => userTitles.has((it?.title || '').toString().toLowerCase()) || (it?.path || '').toString().toLowerCase().includes('userapplication'));
    if (users.length) {
      pushHeader('USER MANAGEMENT');
      users.forEach(pushItem);
    }

    // Rest
    let rest = list.filter((it) => {
      const t = (it?.title || '').toString().toLowerCase();
      if (t.includes('dashboard')) return false;
      if (userTitles.has(t)) return false;
      if ((it?.path || '').toString().toLowerCase().includes('userapplication')) return false;
      return true;
    });
    // Replace Ingredients group with a single direct link to Raw Ingredients
    // Collapse Recipe group to a single /recipe link (Add Recipe is a button inside the listing)
    rest = rest.map((it) => {
      const t = (it?.title || '').toString().toLowerCase().trim();
      if (t === 'ingredients') {
        return {
          ...it,
          path: '/ingredients/rawingredient',
          submenu: [],
          class: (it?.class || '').replace(/\bhas-sub\b/, '').trim()
        };
      }
      if (t === 'recipe' || t === 'recipe management') {
        return {
          ...it,
          path: '/recipe',
          submenu: [],
          class: (it?.class || '').replace(/\bhas-sub\b/, '').trim()
        };
      }
      return it;
    });

    // Keep Supplements directly under Recipe in MODULES.
    rest = this.moveMenuItemAfter(rest, 'supplements', 'recipe');

    if (rest.length) {
      pushHeader('MODULES');
      rest.forEach(pushItem);
    }
    return out;
  }

  /** Moves an item (by title) to sit immediately after another item. */
  private moveMenuItemAfter(items: any[], moveTitle: string, afterTitle: string): any[] {
    const normalize = (t: any) => (t || '').toString().toLowerCase().trim();
    const moveIdx = items.findIndex((it) => {
      const t = normalize(it?.title);
      return t === moveTitle || t === `all ${moveTitle}` || t.includes(moveTitle);
    });
    if (moveIdx === -1) return items;

    const afterIdx = items.findIndex((it) => {
      const t = normalize(it?.title);
      return t === afterTitle || t === `${afterTitle} management`;
    });
    if (afterIdx === -1) return items;

    const next = items.slice();
    const [item] = next.splice(moveIdx, 1);
    const insertAt = moveIdx < afterIdx ? afterIdx : afterIdx + 1;
    next.splice(insertAt, 0, item);
    return next;
  }

  async loadMenuItems() {
    this.loggedInUser = this.authService.getLoggedInUser();
    this.isMenuLoading = true;
    this.cdr.detectChanges();
    
    try {
      // Clear user-specific cache to ensure fresh data for current role
      if (this.loggedInUser && this.loggedInUser.id) {
        this.enhancedMenuService.clearUserCache(this.loggedInUser.id);
      }
      
      // Use enhanced menu service for hierarchical filtering
      this.menuItems = await this.enhancedMenuService.getFilteredMenu();
      console.log('Using enhanced menu with', this.menuItems.length, 'items for role:', this.loggedInUser.currentRoleId);
      this.renderItems = this.buildRenderItems(this.menuItems);
      this.isMenuLoading = false;
      this.cdr.detectChanges();
      this.queueLucideCreateIcons();
    } catch (error) {
      console.error('Error loading enhanced menu, falling back to static routes:', error);

      // Fallback to the permission-filtered routes already stored in session.
      // Avoid static ROUTES here because that would expose modules without privilege checks.
      const dynamicRoutes = this.authService.getVerticalRoutes();
      this.menuItems = Array.isArray(dynamicRoutes) ? dynamicRoutes : [];
      console.log('Using session routes fallback with', this.menuItems.length, 'items');
      this.renderItems = this.buildRenderItems(this.menuItems);
      this.isMenuLoading = false;
      this.cdr.detectChanges();
      this.queueLucideCreateIcons();
      return;
    }
  }

  ngAfterViewInit() {

    this.configSub = this.configService.templateConf$.subscribe((templateConf) => {
      if (templateConf) {
        this.config = templateConf;
      }
      this.loadLayout();
      this.cdr.markForCheck();

    });

    this.layoutSub = this.layoutService.overlaySidebarToggle$.subscribe(
      collapse => {
        if (this.config.layout.menuPosition === "Side") {
          this.collapseSidebar = collapse;
        }
      });

    // Ensure Lucide runs after initial sidebar DOM paint
    this.queueLucideCreateIcons();

    // Listen for role changes and refresh menu
    this.authService.roleChangeSubject$.subscribe(async (newRole) => {
      console.log('Role changed to:', newRole, 'refreshing menu...');
      console.log('Current user:', this.loggedInUser);
      
      // Clear user-specific cache to ensure fresh data for new role
      if (this.loggedInUser && this.loggedInUser.id) {
        this.enhancedMenuService.clearUserCache(this.loggedInUser.id);
        console.log('Cleared cache for user:', this.loggedInUser.id);
      }
      
      await this.loadMenuItems();
      console.log('Menu items after role change:', this.menuItems);
      this.cdr.detectChanges();
      this.renderItems = this.buildRenderItems(this.menuItems);
      this.queueLucideCreateIcons();
    });

  }


  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout((() => {
      this.innerWidth = event.target.innerWidth;
      this.loadLayout();
    }).bind(this), 500);
  }

  loadLayout() {
    const activeMenuItems = Array.isArray(this.menuItems) ? this.menuItems : [];
    this.renderItems = this.buildRenderItems(activeMenuItems);
    this.queueLucideCreateIcons();


    if (this.config.layout.sidebar.backgroundColor === 'white') {
      this.logoUrl = 'assets/img/logo-dark.png';
    }
    else {
      this.logoUrl = 'assets/img/logo.png';
    }

    if (this.config.layout.sidebar.collapsed) {
      this.collapseSidebar = true;
    }
    else {
      this.collapseSidebar = false;
    }
  }

  toggleSidebar() {
    let conf = this.config;
    conf.layout.sidebar.collapsed = !this.config.layout.sidebar.collapsed;
    this.configService.applyTemplateConfigChange({ layout: conf.layout });

    setTimeout(() => {
      this.fireRefreshEventOnWindow();
    }, 300);
  }

  fireRefreshEventOnWindow = function () {
    const evt = document.createEvent("HTMLEvents");
    evt.initEvent("resize", true, false);
    window.dispatchEvent(evt);
  };

  CloseSidebar() {
    this.layoutService.toggleSidebarSmallScreen(false);
  }

  isTouchDevice() {

    const isMobile = this.deviceService.isMobile();
    const isTablet = this.deviceService.isTablet();

    if (isMobile || isTablet) {
      this.perfectScrollbarEnable = false;
    }
    else {
      this.perfectScrollbarEnable = true;
    }

  }


  ngOnDestroy() {
    if (this.layoutSub) {
      this.layoutSub.unsubscribe();
    }
    if (this.configSub) {
      this.configSub.unsubscribe();
    }
    if (this.menuRefreshSub) {
      this.menuRefreshSub.unsubscribe();
    }
    if (this.lucideTimer) {
      clearTimeout(this.lucideTimer);
      this.lucideTimer = null;
    }

  }
}
